import { getSubjectLabels } from '@/lib/settings';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { api } from '../../lib/api/core';
// Spiegelt das Backend, damit die Vorschau-URL der oeffentlichen Route entspricht.
import { slugify } from '../../lib/slugify';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

// Lazy import breaks the CmsContentEditor -> SlotEditor -> StandortFaecherField
// -> CmsContentEditor import cycle.
const CmsContentEditor = React.lazy(() => import('./CmsContentEditor'));

interface FachItem {
  id: number;
  name: string;
  code?: string | null;
  standort_ids?: number[];
}

interface StandortItem {
  id: number;
  name: string;
  code?: string | null;
}

/**
 * Per-standort fach manager: lists the fächer assigned to this standort (from
 * the Stammdaten fach<->standort assignment), lets you toggle each one's
 * visibility on the standort page (stored in the standort content as a list of
 * hidden fach ids) and edit its combination content inline.
 */
export const StandortFaecherField: React.FC<{
  standortId: number;
  /** Hidden fach ids (the field value in the standort content). */
  value: number[];
  onChange: (v: number[]) => void;
}> = ({ standortId, value, onChange }) => {
  const [faecher, setFaecher] = useState<FachItem[] | null>(null);
  const [standort, setStandort] = useState<StandortItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FachItem | null>(null);

  const hidden = Array.isArray(value) ? value : [];

  const load = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        api.get<FachItem[]>('/api/faecher?active_only=false'),
        api.get<StandortItem[]>('/api/standorte?active_only=false'),
      ]);
      setFaecher(Array.isArray(f) ? f : []);
      setStandort((Array.isArray(s) ? s : []).find((x) => x.id === standortId) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : `${getSubjectLabels().plural} konnten nicht geladen werden`);
    }
  }, [standortId]);

  useEffect(() => { void load(); }, [load]);

  const assigned = useMemo(() => {
    if (!faecher) return [];
    // Mirror the website: a fach with no standort_ids is offered everywhere.
    return faecher.filter((f) => {
      const ids = f.standort_ids || [];
      return ids.length === 0 || ids.includes(standortId);
    });
  }, [faecher, standortId]);

  const toggle = (fachId: number, visible: boolean) => {
    if (visible) onChange(hidden.filter((x) => x !== fachId));
    else if (!hidden.includes(fachId)) onChange([...hidden, fachId]);
  };

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!faecher) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Lädt …
      </div>
    );
  }
  if (assigned.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Diesem Standort sind (über die Stammdaten) keine {getSubjectLabels().plural} zugeordnet.
      </p>
    );
  }

  const previewPath = (fach: FachItem) =>
    standort
      ? `/standort/${slugify(standort.code || standort.name)}/${slugify(fach.code || fach.name)}`
      : undefined;

  return (
    <div className="divide-y rounded-md border">
      {assigned.map((fach) => {
        const visible = !hidden.includes(fach.id);
        return (
          <div key={fach.id} className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="truncate text-sm">{fach.name}</span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Switch
                  checked={visible}
                  onCheckedChange={(v: boolean) => toggle(fach.id, v)}
                  aria-label="Auf der Standortseite anzeigen"
                />
                Anzeigen
              </label>
              <Button type="button" variant="outline" size="sm" onClick={() => setEditing(fach)}>
                <Pencil className="mr-2 h-4 w-4" /> Inhalte
              </Button>
            </div>
          </div>
        );
      })}

      <Dialog open={!!editing} onOpenChange={(o: boolean) => { if (!o) setEditing(null); }}>
        <DialogContent size="full" className="h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing && standort ? `${standort.name} × ${editing.name}` : ''}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              }
            >
              <CmsContentEditor
                key={`${standortId}-${editing.id}`}
                slug={`standort-fach/${standortId}-${editing.id}`}
                title={standort ? `${standort.name} × ${editing.name}` : editing.name}
                description={`Standort × ${getSubjectLabels().singular}-Inhalte`}
                previewPath={previewPath(editing)}
              />
            </Suspense>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StandortFaecherField;
